import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UniHeader } from "./_lmsShared";
import { Search, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university`;

const STATUS_TONE = {
  "Lifetime Alumni": "bg-purple-100 text-purple-800",
  "Alumni Dues Current": "bg-emerald-100 text-emerald-800",
  "Active Graduate": "bg-sky-100 text-sky-800",
  "Inactive": "bg-slate-100 text-slate-600",
  "Suspended": "bg-rose-100 text-rose-800",
};

export default function UniversityRegistry() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [credential, setCredential] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (credential) params.credential = credential;
      const r = await axios.get(`${API}/alumni/registry`, { params });
      setItems(r.data?.items || []);
    } catch (e) { toast.error("Could not load registry"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Graduate Registry" back="/university" />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="uni-registry">
        <div className="flex items-center gap-2 text-purple-900">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-xs uppercase tracking-wide">Credential Verification Registry</span>
        </div>
        <h1 className="font-display text-4xl text-slate-900 mt-1">Verify a KISSED University Graduate</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Search by name or certificate number to verify any KISSED University credential. This registry is the official source of credential status.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="uni-registry-search">
          <input className="md:col-span-6 rounded-xl border border-slate-200 px-3 h-10 text-sm" placeholder="Search by name or certificate number…" value={q} onChange={(e) => setQ(e.target.value)} />
          <input className="md:col-span-5 rounded-xl border border-slate-200 px-3 h-10 text-sm" placeholder="Filter by credential" value={credential} onChange={(e) => setCredential(e.target.value)} />
          <button className="md:col-span-1 rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-10 text-sm inline-flex items-center justify-center" type="submit"><Search className="w-4 h-4" /></button>
        </form>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No results.</div>
          ) : (
            <table className="w-full text-sm" data-testid="uni-registry-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-4 py-2">Certificate #</th><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Credential</th><th className="text-left px-4 py-2">Year</th><th className="text-left px-4 py-2">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r, i) => (
                  <tr key={i} data-testid={`uni-registry-row-${r.certificate_number}`}>
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-900">{r.certificate_number}</td>
                    <td className="px-4 py-2 text-slate-900">{r.student_name}</td>
                    <td className="px-4 py-2 text-slate-700">{r.credential}</td>
                    <td className="px-4 py-2 text-slate-700">{r.graduation_year}</td>
                    <td className="px-4 py-2"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-700"}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
